<script lang="ts" setup>
import { useValidator } from '@/composeables';
import type { IAlertMessage } from '@/objects';
import type { ValidatorDef } from '@/objects/gui';
import { type DateType } from '@/utilities/dateutils';
import { watch } from 'vue';

const props = withDefaults(
    defineProps<{
        /**
         * The value to display
         * @default null
         */
        value: Date | null;

        /**
         * The format of the date
         * @default 'DATETIME'
         */
        format?: DateType;

        /**
         * If the text should be monospace
         * @default true
         */
        monospace?: boolean;

        /**
         * If the min value should be shown or not rendered at all
         */
        dontshowminvalue?: boolean;

        validator?: ValidatorDef | null;

        /**
         * The row data if it is used in a grid
         */
        row?: any;
    }>(),
    {
        value: null,
        format: 'DATETIME',
        monospace: true,
    },
);

const emit = defineEmits<{
    /**
     * Emits the validation result of the field. This is emitted whenever the validation result changes. The validation result is 'valid', 'warning' or 'error'. The message is the validation message from the validator, if any.
     */
    (e: 'validation', message: IAlertMessage | null): void;
}>();

const { ValidateMessage } = useValidator(props.validator ?? null, props.value, props.row);

emit('validation', ValidateMessage.value ?? null);
</script>
<template>
    <span
        class="field-renderer-datetime text-nowrap"
        :class="{ 'font-monospace': monospace }"
        :type="format"
        v-if="!(dontshowminvalue && value?.getFullYear() == 1)"
    >
        {{ $td(value, format)?.replace(',', '.') }}
    </span>
</template>
